import React from 'react';
import { ArrowRight, Smartphone } from 'lucide-react';
import { motion } from 'framer-motion';
export function ProgramsSection() {
  return <section id="programs" className="py-20 md:py-32 px-6 bg-teal-light relative overflow-hidden" data-id="element-330">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-331">
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-teal/10 rounded-full blur-3xl" data-id="element-332"></div>
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-white to-transparent" data-id="element-333"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10" data-id="element-334">
        <div className="text-center mb-16" data-id="element-335">
          <h2 className="text-3xl md:text-5xl font-extrabold text-navy mb-6 tracking-tight" data-id="element-336">
            Programs built for every stage.
          </h2>
          <p className="text-lg md:text-xl text-navy/70 max-w-2xl mx-auto font-medium" data-id="element-337">
            From 6 weeks to school readiness. Aligned with the Early Years
            Learning Framework.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16" data-id="element-338">
          {/* Nursery */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white rounded-[24px] shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden flex flex-col border-t-8 border-blue group" data-id="element-339">
            <div className="aspect-[4/3] bg-sand/30 relative flex items-center justify-center overflow-hidden" data-id="element-340">
              <img src="https://cdn.magicpatterns.com/uploads/cpvEPqgrngxUYT2o3QmMvj/nursery-children-playing-with-teacher-in-the-classroom.jpg" alt="Nursery children playing with educator at Funtastic Kidz" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-341" />
              <div className="absolute inset-0 bg-cream/5 mix-blend-overlay" data-id="element-342"></div>
            </div>
            <div className="p-8 flex flex-col flex-1 relative z-10 bg-white" data-id="element-343">
              <div className="inline-block bg-blue/10 text-blue px-4 py-1.5 rounded-full text-xs font-extrabold mb-4 w-fit tracking-wide uppercase" data-id="element-344">
                From 6 weeks
              </div>
              <h3 className="text-2xl font-extrabold text-navy mb-4" data-id="element-345">
                Nursery / Infants
              </h3>
              <p className="text-navy/80 mb-8 flex-1 font-medium leading-relaxed" data-id="element-346">
                Calm, home-like rooms with sensory play, music, outdoor
                experiences, gentle routines, and 1:4 educator-to-child support.
              </p>
              <div className="flex flex-col gap-4 mt-auto pt-6 border-t border-sand/50" data-id="element-347">
                <a href="#tours" className="text-navy font-bold hover:text-blue transition-colors flex items-center gap-2 group" data-id="element-348">
                  Learn about this program{' '}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-349" />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Toddlers */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-[24px] shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden flex flex-col border-t-8 border-coral group" data-id="element-350">
            <div className="aspect-[4/3] bg-sand/30 relative flex items-center justify-center overflow-hidden" data-id="element-351">
              <img src="https://cdn.magicpatterns.com/uploads/kEJPbWkajEwcVMRKQddBhV/children-playing-together-in-the-classroom-in-kindergarten-.jpg" alt="Toddlers playing together at Funtastic Kidz" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-352" />
              <div className="absolute inset-0 bg-cream/5 mix-blend-overlay" data-id="element-353"></div>
            </div>
            <div className="p-8 flex flex-col flex-1 relative z-10 bg-white" data-id="element-354">
              <div className="inline-block bg-coral/10 text-coral px-4 py-1.5 rounded-full text-xs font-extrabold mb-4 w-fit tracking-wide uppercase" data-id="element-355">
                2-3 years
              </div>
              <h3 className="text-2xl font-extrabold text-navy mb-4" data-id="element-356">
                Toddlers
              </h3>
              <p className="text-navy/80 mb-8 flex-1 font-medium leading-relaxed" data-id="element-357">
                Hands-on learning. Storytelling. Outdoor play. Language and
                confidence develop fast at this age — our routines are built
                around it.
              </p>
              <div className="flex flex-col gap-4 mt-auto pt-6 border-t border-sand/50" data-id="element-358">
                <a href="#tours" className="text-navy font-bold hover:text-coral transition-colors flex items-center gap-2 group" data-id="element-359">
                  Learn about this program{' '}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-360" />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Kinder */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="bg-white rounded-[24px] shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden flex flex-col border-t-8 border-yellow group" data-id="element-361">
            <div className="aspect-[4/3] bg-sand/30 relative flex items-center justify-center overflow-hidden" data-id="element-362">
              <img src="https://cdn.magicpatterns.com/uploads/cgkdVezvMH87xH8pYRd5CT/kid-is-learning-in-class.jpg" alt="Child learning and reading at Funtastic Kidz kinder program" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-363" />
              <div className="absolute inset-0 bg-cream/5 mix-blend-overlay" data-id="element-364"></div>
            </div>
            <div className="p-8 flex flex-col flex-1 relative z-10 bg-white" data-id="element-365">
              <div className="inline-block bg-yellow/20 text-yellow-600 px-4 py-1.5 rounded-full text-xs font-extrabold mb-4 w-fit tracking-wide uppercase" data-id="element-366">
                3-5 years
              </div>
              <h3 className="text-2xl font-extrabold text-navy mb-4" data-id="element-367">
                Pre-Kinder & Kinder
              </h3>
              <p className="text-navy/80 mb-8 flex-1 font-medium leading-relaxed" data-id="element-368">
                School-readiness program aligned with EYLF + VEYLDF. Literacy,
                numeracy, problem-solving.
              </p>
              <div className="flex flex-col gap-4 mt-auto pt-6 border-t border-sand/50" data-id="element-369">
                <a href="#tours" className="text-navy font-bold hover:text-yellow-600 transition-colors flex items-center gap-2 group" data-id="element-370">
                  Learn about this program{' '}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-371" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Merged Parent Comms Feature */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="bg-blue-deep rounded-[24px] p-8 md:p-12 shadow-2xl relative overflow-hidden" data-id="element-372">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue rounded-full blur-3xl opacity-50 -z-10" data-id="element-373"></div>

          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 relative z-10" data-id="element-374">
            <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-yellow shrink-0 shadow-lg" data-id="element-375">
              <Smartphone size={40} strokeWidth={1.5} data-id="element-376" />
            </div>
            <div className="flex-1 text-center md:text-left" data-id="element-377">
              <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-3" data-id="element-378">
                See your child's day, every day.
              </h3>
              <p className="text-white/80 font-medium text-lg" data-id="element-379">
                Daily photo, meal, nap, and milestone updates via our parent
                app. Secure sign-in and direct educator messaging included.
              </p>
            </div>
            <div className="shrink-0 w-full md:w-auto" data-id="element-380">
              <a href="#tours" className="block w-full md:w-auto bg-white text-blue-deep px-8 py-4 rounded-btn font-bold text-lg hover:bg-sand transition-colors text-center shadow-lg" data-id="element-381">
                Book a tour
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}